import { ContractResponseError } from "@spark/web";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { useQueryError } from "../lib/query-client.ts";
import { redirectToLogin } from "../lib/redirect-to-login.ts";
import { PageNotFound } from "../views/page-not-found/PageNotFound.tsx";
import { ErrorModal } from "./ErrorModal.tsx";

function useErrorInterceptor() {
	const [statusCode, setStatusCode] = useState<number | null>(null);
	const [error, setError] = useState<Error | null>(null);

	useQueryError((error: Error) => {
		if (error instanceof ContractResponseError) {
			setStatusCode(error.status);
		}
		setError(error);
		throw error;
	});

	const clearError = useCallback(() => {
		setError(null);
		setStatusCode(null);
	}, []);

	return { error, statusCode, clearError };
}

export function ErrorBoundary({
	children,
}: React.PropsWithChildren): React.JSX.Element | undefined {
	const location = useLocation();
	const { error, statusCode, clearError } = useErrorInterceptor();

	// `location` is the trigger, not a value we read: the effect exists to clear
	// the error on every navigation.
	useEffect(() => {
		clearError();
	}, [location, clearError]);

	if (statusCode === 401) {
		redirectToLogin();
		return;
	}

	if (statusCode === 404) {
		return <PageNotFound />;
	}

	return (
		<>
			<ErrorModal error={error} onClose={clearError} />
			{children}
		</>
	);
}
